import dayjs, { formatDateString } from '~libs/dayjs';

import { COPY_STATUS, REVIEW_STATE } from '~support-at-home/models';
import type { Section, SectionItem } from '~support-at-home/models';

export const validatePricePlan: (values: any) => boolean = ({ price_plan_id, price_plan_revision_id }) => {
  if (!price_plan_id || !price_plan_revision_id) return false;
  return true;
};

export const validateDateRange: (values: any) => boolean = ({ start_date, end_date }) => {
  if (!start_date || !end_date) return false;

  const start = dayjs(start_date);
  const end = dayjs(end_date);

  return end.isSameOrAfter(start);
};

/**
 * Blocks a save while any budget line is dated outside the budget itself.
 *
 * Nothing enforced this before, at either layer, so a line could sit entirely outside the period of
 * the budget paying for it and save without complaint. The message names the budget's own range,
 * because "invalid" leaves the provider guessing at dates the form already knows.
 *
 * The narrower rule - a line must also sit inside the period its Support at Home service is
 * available for - needs the service reference data and is enforced on the row itself and again on
 * save by the API.
 *
 * Lines that will not survive the save are not the provider's to fix first, and both ways a line
 * can be on its way out are skipped. The API's own assertion is drawn the same way - it runs on
 * what survives reconciliation, precisely so a line being soft-deleted or dropped never blocks the
 * save that removes it - and the web check has to agree with it or it blocks saves the API would
 * have allowed.
 */
export const validateSectionItemDates = ({ sections, start_date, end_date }: any): true | string => {
  if (!Array.isArray(sections) || !start_date || !end_date) return true;

  const budgetStart = dayjs(start_date);
  const budgetEnd = dayjs(end_date);

  for (const section of sections as Section[]) {
    for (const item of (section.items ?? []) as SectionItem[]) {
      // Dropped from the target price plan on copy, or reconciled off the plan on save. Neither
      // row's dates are editable, and the refresh control clips to the service period rather than
      // to the budget, so a provider told to fix one has no control that can reach it.
      if (item.copy_status === COPY_STATUS.REMOVED) continue;
      if (item.review_state === REVIEW_STATE.NOT_ON_PLAN) continue;
      if (!item.start_date || !item.end_date) continue;

      const outside =
        dayjs(item.start_date).isBefore(budgetStart, 'day') || dayjs(item.end_date).isAfter(budgetEnd, 'day');

      if (outside) {
        return (
          `A budget line is dated ${formatDateString(item.start_date)} - ${formatDateString(item.end_date)}, ` +
          `outside the budget period ${formatDateString(start_date)} - ${formatDateString(end_date)}.`
        );
      }
    }
  }

  return true;
};
